import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Search, X, Gavel, Wallet, Loader2 } from "lucide-react";

export default function GlobalSearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ icra: [], cari: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const close = () => { setOpen(false); setQ(""); setRes({ icra: [], cari: [] }); setLoading(false); };

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) { setRes({ icra: [], cari: [] }); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(term)}`);
        setRes(r.data || { icra: [], cari: [] });
      } catch {
        setRes({ icra: [], cari: [] });
      }
      setLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [q, open]);

  if (!user || user.role === "izleyici") return null;

  const goIcra = (r) => { close(); navigate(`/icra/${r.id}`); };
  const goCari = (r) => { close(); navigate(`/cari/${encodeURIComponent(r.musteri)}`); };
  const hasResults = res.icra.length > 0 || res.cari.length > 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="global-search-open"
        aria-label="Hızlı ara"
        className="p-2 rounded-sm hover:bg-[#EFE7D6] transition-colors duration-150"
      >
        <Search className="w-5 h-5 text-[#8C8377]" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[80]" data-testid="global-search-overlay">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-[1px]" onClick={close} />
          <div
            className="absolute left-3 right-3 top-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-[520px] bg-white border border-[#E7DECC] rounded-xl shadow-2xl overflow-hidden"
            data-testid="global-search-panel"
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E7DECC]">
              <Search className="w-5 h-5 text-[#B8933F] shrink-0" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="global-search-input"
                placeholder="Müşteri adı veya dosya no ara…"
                className="flex-1 bg-transparent outline-none text-base placeholder:text-[#B0A692]"
              />
              {loading && <Loader2 className="w-4 h-4 text-[#8C8377] animate-spin" />}
              <button onClick={close} data-testid="global-search-close" aria-label="Kapat" className="p-1 rounded-sm hover:bg-[#EFE7D6]">
                <X className="w-4 h-4 text-[#8C8377]" />
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto" data-testid="global-search-results">
              {q.trim().length < 2 ? (
                <div className="px-4 py-8 text-center text-sm text-[#8C8377]">En az 2 harf yazın…</div>
              ) : !loading && !hasResults ? (
                <div className="px-4 py-8 text-center text-sm text-[#8C8377]" data-testid="global-search-empty">"{q}" için sonuç bulunamadı.</div>
              ) : (
                <>
                  {res.icra.length > 0 && (
                    <div>
                      <div className="px-4 py-1.5 bg-[#F3ECDD] kicker border-b border-[#E7DECC]">İCRA DOSYALARI</div>
                      {res.icra.map((r) => (
                        <button
                          key={r.id}
                          onClick={() => goIcra(r)}
                          data-testid={`global-search-icra-${r.id}`}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#EFE7D6] border-b border-[#F0E9DA] text-left transition-colors duration-150"
                        >
                          <Gavel className="w-4 h-4 text-[#A63A3A] shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{r.musteri || "—"}</div>
                            <div className="text-xs text-[#8C8377] truncate">{[r.dosya_no, r.icra_dairesi, r.il].filter(Boolean).join(" · ")}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {res.cari.length > 0 && (
                    <div>
                      <div className="px-4 py-1.5 bg-[#F3ECDD] kicker border-b border-[#E7DECC]">CARİ MÜŞTERİLER</div>
                      {res.cari.map((r, i) => (
                        <button
                          key={`c-${i}`}
                          onClick={() => goCari(r)}
                          data-testid={`global-search-cari-${i}`}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#EFE7D6] border-b border-[#F0E9DA] text-left transition-colors duration-150"
                        >
                          <Wallet className="w-4 h-4 text-[#B8933F] shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{r.musteri}</div>
                            {r.tc_no ? <div className="text-xs text-[#8C8377] truncate">TC: {r.tc_no}</div> : null}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
